import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

// Static, crawlable content for the public homepage. The listings above it
// load from Firestore at runtime, so without this the prerendered homepage
// has almost no text for search engines.

const popularAreas = [
    "East Ham", "Ilford", "Wembley", "Stratford", "Harrow", "Southall",
    "Barking", "Manor Park", "Forest Gate", "Hounslow", "Croydon", "Canary Wharf",
];

const areaSlug = (area) => area.toLowerCase().replace(/\s+/g, '-');

const guides = [
    { to: "/indian-areas-in-london", label: "Best areas for Indians in London" },
    { to: "/first-week-in-uk-checklist", label: "Your first week in the UK: checklist" },
    { to: "/uk-essentials-for-indians", label: "UK essentials checklist for new arrivals" },
    { to: "/airport-cabs", label: "Airport cabs from Heathrow & Gatwick" },
    { to: "/uk-jobs-for-indians", label: "Finding jobs in the UK" },
    { to: "/money-transfer-to-india", label: "Sending money to India" },
    { to: "/visa-and-immigration-support", label: "Visa & immigration support" },
];

// Content per page: homepage (rooms) and /roommates
const variants = {
    rooms: {
        heading: "Rooms and flatmates for Indians in London",
        intro: [
            "IndianFlatmates helps Indian students and professionals find rooms to rent, flatshares and paying guest (PG) accommodation in London and across the UK. Whether you have just arrived for university, moved for a new job, or have a spare room to fill, you can connect with people who share your language, food and culture.",
            "Room owners post their listings with rent, bills and photos, and people looking for a room post a short profile with their budget and preferred areas. Everyone can browse and message each other directly, so you can arrange a viewing and move in quickly.",
        ],
        faqs: [
            {
                q: "How can I find an Indian flatmate in London?",
                a: "Browse the room listings on this page, or visit the Roommates page to see Indians looking for a room. You can search by area, message people directly on the site, or post your own room or roommate request for free.",
            },
            {
                q: "Is IndianFlatmates suitable for students and working professionals?",
                a: "Yes. Most people on the site are Indian students and young professionals looking for shared rooms, flatshares and paying guest (PG) accommodation in London and across the UK.",
            },
            {
                q: "Is it free to post a room or a roommate request?",
                a: "Yes. Posting is free and you can have up to 4 active room listings at a time. An optional premium plan shows phone numbers and adds direct WhatsApp and call buttons.",
            },
            {
                q: "Which areas of London are popular with Indians?",
                a: "East Ham, Ilford, Barking and Manor Park in East London are popular with South Indian students and professionals, while Wembley, Harrow and Southall have long-established Indian communities. See our guide to the best areas for Indians in London for details.",
            },
            {
                q: "Can you find a flatmate for my room?",
                a: "Yes. With our Fill My Room service we promote your listing, screen enquiries and arrange viewings. You pay one week's rent only when your room is filled.",
            },
        ],
    },
    roommates: {
        heading: "Find an Indian roommate for your spare room",
        intro: [
            "These are Indian students and working professionals looking for a room or flatshare in London and across the UK. Each profile shows their budget, the areas they want to live in, their profession and what they are looking for, so you can quickly see who would fit your home.",
            "If you have a spare room, message people directly on the site or post your room for free so they can find you. Many people here are looking for flatmates who share their food habits, language or culture, such as vegetarian households or Telugu, Tamil and Hindi speakers.",
        ],
        faqs: [
            {
                q: "How do I find a flatmate for my spare room?",
                a: "Browse the roommate profiles on this page and message anyone whose budget and preferred areas match your room. You can also post your room for free so people looking in your area can contact you.",
            },
            {
                q: "What does a roommate profile include?",
                a: "Each profile shows the person's budget per month, the areas they want to live in, their profession, and a short description of the room and flatmates they are looking for.",
            },
            {
                q: "Is it free to contact people looking for a room?",
                a: "Yes, messaging on the site is free. An optional premium plan shows phone numbers and adds direct WhatsApp and call buttons.",
            },
            {
                q: "How can I stay safe when choosing a flatmate?",
                a: "Meet in person or on a video call before agreeing anything, check ID, and never pay or ask for money before a viewing. If you rent under an assured shorthold tenancy, the deposit must be protected in a government-approved deposit protection scheme.",
            },
            {
                q: "Can you find a flatmate for me?",
                a: "Yes. With our Fill My Room service we promote your room, screen enquiries and arrange viewings. You pay one week's rent only when your room is filled.",
            },
        ],
    },
};

const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
});

function HomeSeoContent({ variant = "rooms" }) {
    const { heading, intro, faqs } = variants[variant];
    return (
        <section className="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8 text-gray-700 dark:text-gray-300">
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqSchema(faqs))}</script>
            </Helmet>

            <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mb-3">{heading}</h2>
            {intro.map((para, i) => (
                <p key={i} className={i === intro.length - 1 ? "mb-8" : "mb-3"}>{para}</p>
            ))}

            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Popular areas for Indian flatmates</h2>
            <ul className="flex flex-wrap gap-2 mb-8">
                {popularAreas.map((area) => (
                    <li key={area}>
                        <Link
                            to={`/rooms/${areaSlug(area)}`}
                            className="inline-block px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-gray-600"
                        >
                            Rooms in {area}
                        </Link>
                    </li>
                ))}
            </ul>

            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">How it works</h2>
            <ol className="list-decimal list-inside space-y-1 mb-8">
                <li><strong>Browse</strong> rooms and roommate profiles, and search by area.</li>
                <li><strong>Post for free</strong> — list your spare room or share what you are looking for.</li>
                <li><strong>Connect</strong> by messaging on the site, then arrange a viewing.</li>
            </ol>

            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Frequently asked questions</h2>
            <div className="space-y-4 mb-8">
                {faqs.map((f) => (
                    <div key={f.q}>
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100">{f.q}</h3>
                        <p>{f.a}</p>
                    </div>
                ))}
            </div>

            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Guides for Indians moving to the UK</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {guides.map((g) => (
                    <li key={g.to}>
                        <Link to={g.to} className="text-indigo-600 dark:text-indigo-400 hover:underline">{g.label} →</Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

export default HomeSeoContent;
